import { SUPPORT_ADDRESS } from './signInErrors'
/**
 * The sign-in screen's words, in English and Spanish (SHL-25 item 3).
 *
 * ── WHY THE BROWSER DECIDES, NOT THE PERSON ─────────────────────────────────
 *
 * Before sign-in there is no person, so there is no person.locale to read —
 * and asking the database "what language is this address" would be exactly
 * the roster oracle B13 forbids: the answer differs for a known and an unknown
 * address. So the screen follows the BROWSER (navigator.languages, es-* →
 * Spanish), with a visible switch, and remembers a switch in this browser only.
 * After sign-in the person's own locale (103) takes over for everything the
 * hub translates; the two are deliberately not linked.
 *
 * ── DRAFT ───────────────────────────────────────────────────────────────────
 *
 * The Spanish was written by an assistant on 24 Sep and has not been read by
 * a Spanish speaker. It addresses the reader as tú and says "celular" (Peru
 * and Chile both). A native reader must check it before any invitation wave.
 *
 * Only two languages, on purpose: fr-FR is one person, and a French screen
 * nobody has read is worse than an English one.
 */

export type SignInLang = 'en' | 'es'

export const SIGN_IN_LANGS: ReadonlyArray<{ key: SignInLang; label: string; htmlLang: string }> = [
  { key: 'en', label: 'English', htmlLang: 'en' },
  { key: 'es', label: 'Español', htmlLang: 'es' },
]

/** Where a switch is remembered. This browser only; never sent anywhere. */
export const SIGN_IN_LANG_KEY = 'oneconetec.signin.lang'

/**
 * A remembered switch wins; otherwise the first browser language that is one
 * of ours; otherwise English. A browser listing French then Spanish gets
 * Spanish, which is closer than English for that reader.
 */
export function pickSignInLang(
  languages: readonly string[] | null | undefined,
  stored?: string | null,
): SignInLang {
  if (stored === 'en' || stored === 'es') return stored
  for (const l of languages ?? []) {
    const tag = l.toLowerCase()
    if (tag === 'es' || tag.startsWith('es-')) return 'es'
    if (tag === 'en' || tag.startsWith('en-')) return 'en'
  }
  return 'en'
}

export type SignInCopy = {
  titleStart: string
  titleCheckEmail: string
  titleCheckPhone: string
  ledeEmail: string
  ledePhone: string
  /**
   * "We sent a code to <strong>address</strong> …". DEFINITE since 8 Oct:
   * an unknown address is now told so at the first step (signInErrors.ts
   * header), so only a known one reaches here. 25 Sep–8 Oct this read "If
   * <address> is set up…" for everybody (ACC-39, reversed).
   */
  sentEmailBefore: string
  sentEmailAfter: string
  sentPhoneBefore: string
  sentPhoneAfter: string
  /** The code step's footer help: junk folder, resend, then support. */
  noCodeEmail: string
  noCodePhone: string
  /**
   * After sign-in, for a known person whose access is suspended (a leaver, a
   * manual deactivation): App.tsx's `inactive` state. Lives here because it
   * is the signed-out surface's language, not the hub's.
   */
  inactiveTitle: string
  inactiveBody: string
  signOut: string
  emailLabel: string
  emailPlaceholder: string
  countryLabel: string
  mobileLabel: string
  codeLabel: string
  sending: string
  verifying: string
  emailMeACode: string
  textMeACode: string
  signIn: string
  usePhoneInstead: string
  useEmailInstead: string
  sendAnother: string
  differentAddress: string
  differentNumber: string
  legal: string
  notAFullNumber: string
  languageLabel: string
}

// U+2011 (non-breaking hyphen) in "six‑digit", as the English screen had it,
// so "six-" never ends a line on a phone.
export const SIGN_IN_COPY: Record<SignInLang, SignInCopy> = {
  en: {
    titleStart: 'Sign in to OneConetec',
    titleCheckEmail: 'Check your email',
    titleCheckPhone: 'Check your phone',
    ledeEmail: 'No password. We email you a six‑digit code.',
    ledePhone: 'No password. We text a six‑digit code to your mobile.',
    sentEmailBefore: 'We sent a six‑digit code to ',
    sentEmailAfter: '. It expires in one hour. If it is not there, check your junk folder.',
    sentPhoneBefore: 'We texted a six‑digit code to ',
    sentPhoneAfter: '. It can take a minute to arrive.',
    noCodeEmail:
      `No code after a few minutes? Check your junk folder, then send another. Still nothing? Contact OneConetec support at ${SUPPORT_ADDRESS}.`,
    noCodePhone:
      `No text after a few minutes? Send another, or use your email address instead. Still nothing? Contact OneConetec support at ${SUPPORT_ADDRESS}.`,
    inactiveTitle: 'Your access is not active',
    inactiveBody:
      `Your OneConetec account is not active right now. If you think this is an error, contact OneConetec support at ${SUPPORT_ADDRESS}.`,
    signOut: 'Sign out',
    emailLabel: 'Email address',
    emailPlaceholder: 'you@conetec.com',
    countryLabel: 'Country',
    mobileLabel: 'Mobile number',
    codeLabel: 'Six-digit code',
    sending: 'Sending…',
    verifying: 'Verifying…',
    emailMeACode: 'Email me a code',
    textMeACode: 'Text me a code',
    signIn: 'Sign in',
    usePhoneInstead: 'Use my mobile number instead',
    useEmailInstead: 'Use my email address instead',
    sendAnother: 'Send another code',
    differentAddress: 'Use a different address',
    differentNumber: 'Use a different number',
    legal: 'OneConetec is an internal SF Group platform. Access is by invitation only.',
    notAFullNumber: 'That does not look like a full mobile number. Check the country and the digits.',
    languageLabel: 'Language',
  },
  es: {
    titleStart: 'Ingresa a OneConetec',
    titleCheckEmail: 'Revisa tu correo',
    titleCheckPhone: 'Revisa tu teléfono',
    ledeEmail: 'Sin contraseña. Te enviamos por correo un código de seis dígitos.',
    ledePhone: 'Sin contraseña. Te enviamos por SMS un código de seis dígitos a tu celular.',
    sentEmailBefore: 'Enviamos un código de seis dígitos a ',
    sentEmailAfter: '. Vence en una hora. Si no aparece, revisa la carpeta de correo no deseado.',
    sentPhoneBefore: 'Enviamos un código de seis dígitos por SMS al ',
    sentPhoneAfter: '. Puede tardar un minuto en llegar.',
    noCodeEmail:
      `¿No llegó el código en unos minutos? Revisa la carpeta de correo no deseado y pide otro. ¿Sigue sin llegar? Contacta al soporte de OneConetec en ${SUPPORT_ADDRESS}.`,
    noCodePhone:
      `¿No llegó el SMS en unos minutos? Pide otro o usa tu correo electrónico. ¿Sigue sin llegar? Contacta al soporte de OneConetec en ${SUPPORT_ADDRESS}.`,
    inactiveTitle: 'Tu acceso no está activo',
    inactiveBody:
      `Tu cuenta de OneConetec no está activa en este momento. Si crees que es un error, contacta al soporte de OneConetec en ${SUPPORT_ADDRESS}.`,
    signOut: 'Cerrar sesión',
    emailLabel: 'Correo electrónico',
    emailPlaceholder: 'tu@conetec.com',
    countryLabel: 'País',
    mobileLabel: 'Número de celular',
    codeLabel: 'Código de seis dígitos',
    sending: 'Enviando…',
    verifying: 'Verificando…',
    emailMeACode: 'Envíame un código por correo',
    textMeACode: 'Envíame un código por SMS',
    signIn: 'Ingresar',
    usePhoneInstead: 'Usar mi número de celular',
    useEmailInstead: 'Usar mi correo electrónico',
    sendAnother: 'Enviar otro código',
    differentAddress: 'Usar otra dirección',
    differentNumber: 'Usar otro número',
    legal: 'OneConetec es una plataforma interna de SF Group. El acceso es solo por invitación.',
    notAFullNumber: 'Ese no parece un número de celular completo. Revisa el país y los dígitos.',
    languageLabel: 'Idioma',
  },
}

/**
 * Country names for the dial-code picker. English is DIAL_CODES' own label;
 * Spanish is here so phone.ts stays about numbers. A country missing from this
 * map falls back to the English label rather than to nothing.
 */
export const DIAL_LABEL_ES: Record<string, string> = {
  CA: 'Canadá / EE. UU. (+1)',
  CL: 'Chile (+56)',
  PE: 'Perú (+51)',
  AU: 'Australia (+61)',
  GB: 'Reino Unido (+44)',
  FR: 'Francia (+33)',
  NZ: 'Nueva Zelanda (+64)',
}

export function dialLabel(d: { country: string; label: string }, lang: SignInLang): string {
  return lang === 'es' ? (DIAL_LABEL_ES[d.country] ?? d.label) : d.label
}
